import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Shield, AlertCircle, CheckCircle2, ExternalLink } from "lucide-react";
import Button from "../../../../shared/ui/button/Button";
import Input from "../../../../shared/ui/form/input/InputField";
import Label from "../../../../shared/ui/form/Label";
import { enableMfa, startMfaSetup } from "../../application/mfa";
import { useTherapist } from "../../../../context/useTherapist";
import { useNavigate, Link } from "react-router";
import AuthLayout from "./AuthPageLayout";
import PageMeta from "../../../../shared/ui/common/PageMeta";

export default function MfaSetupPage() {
  const { t } = useTranslation();
  const { therapist, setTherapist } = useTherapist();
  const [step, setStep] = useState<"initial" | "setup" | "success">("initial");
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Wait for the account: before it loads, MFA may already be on.
    if (!therapist) return;
    if (therapist.mfa_enabled) {
      navigate("/");
    } else {
        handleStartSetup();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [therapist]);

  const handleStartSetup = async () => {
    setLoading(true);
    setError(null);
    try {
      const setup = await startMfaSetup();
      setQrCode(setup.qr_code_base64);
      setSecret(setup.secret);
      setStep("setup");
    } catch {
      setError(t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndEnable = async () => {
    setLoading(true);
    setError(null);
    try {
      await enableMfa(totpCode);
      
      if (therapist) {
          setTherapist({
              ...therapist,
              mfa_enabled: true
          });
      }
      
      setStep("success");
    } catch {
      setError(t("auth.errorInvalidMfa"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageMeta title={t("profile.mfa.title")} />
      <AuthLayout>
        <div className="w-full max-w-md mx-auto">
          <div className="mb-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-500/10 text-brand-500 mb-4">
              <Shield className="w-8 h-8" />
            </div>
            <h1 className="text-3xl font-bold text-content-primary mb-2">
              {t("profile.mfa.title")}
            </h1>
            <p className="text-content-tertiary">
              {t("profile.mfa.description")}
            </p>
          </div>

          <div className="bg-white dark:bg-white/5 rounded-3xl border border-gray-200/50 dark:border-white/10 p-8 shadow-theme-lg">
            {step === "setup" && (
              <div className="space-y-6">
                <div className="flex flex-col items-center gap-4 p-6 bg-gray-50 dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/5">
                  <p className="text-sm font-medium text-center text-gray-600 dark:text-gray-300">
                    {t("profile.mfa.qrCodeDesc")}
                  </p>
                  {qrCode && (
                    <div className="p-4 bg-white rounded-xl shadow-sm">
                      <img src={`data:image/png;base64,${qrCode}`} alt="MFA QR Code" className="w-48 h-48" />
                    </div>
                  )}
                  {secret && (
                    <div className="text-center space-y-2">
                        <code className="text-xs font-mono bg-gray-200 dark:bg-white/10 px-2 py-1 rounded block">
                        {secret}
                        </code>
                        <Link 
                            to="/google-authenticator-guide" 
                            className="inline-flex items-center gap-1.5 text-sm text-brand-600 dark:text-brand-400 hover:underline mt-2"
                        >
                            {t("profile.mfa.guideLink")}
                            <ExternalLink className="w-3.5 h-3.5" />
                        </Link>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mfaCode">
                    {t("profile.mfa.enterCode")}
                  </Label>
                  <Input
                    id="mfaCode"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    placeholder="000 000"
                    className="text-center text-2xl tracking-widest font-bold"
                  />
                </div>

                {error && (
                  <div className="p-3 rounded-xl bg-error-50 dark:bg-error-500/10 border border-error-100 dark:border-500/20 text-error-600 dark:text-error-400 text-sm flex gap-2 items-center">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </div>
                )}

                <Button 
                    className="w-full" 
                    onClick={handleVerifyAndEnable} 
                    loading={loading} 
                    disabled={totpCode.length < 6}
                >
                  {t("profile.mfa.verifyAndEnable")}
                </Button>
              </div>
            )}

            {step === "success" && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center gap-4 py-8 text-center"
              >
                <div className="w-16 h-16 rounded-full bg-success-500/10 text-success-500 flex items-center justify-center">
                  <CheckCircle2 className="w-10 h-10" />
                </div>
                <h5 className="text-xl font-bold text-content-primary">
                  {t("profile.mfa.success")}
                </h5>
                <p className="text-content-tertiary mb-4">
                    Your account is now fully protected.
                </p>
                <Button className="w-full" onClick={() => navigate("/")}>
                   Get Started
                </Button>
              </motion.div>
            )}
            
            {loading && step === "initial" && (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
                </div>
            )}
          </div>
        </div>
      </AuthLayout>
    </>
  );
}
